// src/components/UI/LazyImage.tsx
import React, { useState, useRef, useEffect, ReactNode, HTMLAttributes } from 'react';

interface LazyImageProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onLoad' | 'onError'> {
  src: string;
  alt: string;
  className?: string;
  placeholder?: ReactNode;
  onLoad?: () => void;
  onError?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  placeholder = null,
  onLoad = () => {},
  onError = () => {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isInView, setIsInView] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Create intersection observer for lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.1,
      }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLoad = (): void => {
    setIsLoaded(true);
    onLoad();
  };

  const handleError = (): void => {
    setHasError(true);
    onError();
  };

  // Generate WebP source with fallback
  const getOptimizedSrc = (originalSrc: string): string => {
    if (!originalSrc) return '';

    // If it's a Supabase URL, we can add transformation params
    if (originalSrc.includes('supabase')) {
      // Add width/height params for optimization
      try {
        const url = new URL(originalSrc);
        url.searchParams.set('width', '400');
        url.searchParams.set('height', '400');
        url.searchParams.set('resize', 'cover');
        url.searchParams.set('quality', '80');
        return url.toString();
      } catch {
        return originalSrc;
      }
    }

    return originalSrc;
  };

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`} {...props}>
      {/* Placeholder while loading */}
      {!isLoaded && !hasError && (
        <div
          className={`absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center ${className}`}
        >
          {placeholder ?? <div className="text-gray-400 text-sm">Loading...</div>}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div
          className={`absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center ${className}`}
        >
          <div className="text-gray-500 text-sm text-center">
            <div className="text-xl mb-1">📷</div>
            <div>Image unavailable</div>
          </div>
        </div>
      )}

      {/* Actual image - only load when in view */}
      {isInView && (
        <picture>
          {/* WebP source for supported browsers */}
          <source srcSet={getOptimizedSrc(src)} type="image/webp" />
          {/* Fallback for browsers that don't support WebP */}
          <img
            src={getOptimizedSrc(src)}
            alt={alt}
            className={`transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } ${className}`}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy" // Native lazy loading as backup
            decoding="async" // Improve performance
          />
        </picture>
      )}
    </div>
  );
};

export default LazyImage;
export type { LazyImageProps };
